import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDatabase, faPlus, faSave, faTrash, faPlay,
    faSearch, faCode, faLayerGroup, faTable, faFilter,
    faSortAmountDown, faClock, faInfoCircle, faChevronRight,
    faTerminal, faCube, faSpinner, faNetworkWired,
    faFolder, faFolderOpen, faFileCode, faBoxOpen,
    faChevronDown, faSyncAlt, faFileExcel, faEye, faTools,
    faTimes, faMagic, faHistory, faEraser, faColumns
} from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Spinner, Table, Modal } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';
import ConnectionManager from './ConnectionManager';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

const QueryBuilderPanel = () => {
    // ─── Main State ──────────────────────────────────────────────────────────
    const [queries, setQueries] = useState([]);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeMainTab, setActiveMainTab] = useState('explorer');
    const [editorMode, setEditorMode] = useState('saved'); // 'saved' or 'playground'

    // ─── Explorer State ──────────────────────────────────────────────────────
    const [selectedConn, setSelectedConn] = useState({ ConnName: 'locU', isLocal: true });
    const [connTables, setConnTables] = useState([]);
    const [loadingTables, setLoadingTables] = useState(false);
    const [expandedConns, setExpandedConns] = useState({ 'locU': true });
    const [searchTerm, setSearchTerm] = useState('');

    // ─── Content State ───────────────────────────────────────────────────────
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [playgroundSQL, setPlaygroundSQL] = useState('-- SQL Playground\nSELECT * FROM turn_sucursales LIMIT 10;');
    const [results, setResults] = useState(null);
    const [loadingResults, setLoadingResults] = useState(false);
    const [previewTable, setPreviewTable] = useState(null);

    // ─── Logic ───────────────────────────────────────────────────────────────
    useEffect(() => { init(); }, []);

    const init = async () => {
        setLoading(true);
        await Promise.all([fetchQueries(), fetchConnections()]);
        setLoading(false);
    };

    const fetchQueries = () => new Promise(res => request.Get(API_BASE + 'superadmin/queries', {}, r => { if (r.success) setQueries(r.data || []); res(); }));
    const fetchConnections = () => new Promise(res => request.Get(API_BASE + 'superadmin/connections', {}, r => { if (r.success) setConnections(r.data || []); res(); }));

    const toggleConn = async (conn) => {
        const key = conn.ConnId || conn.ConnName;
        const isExpanded = expandedConns[key];
        setExpandedConns({ ...expandedConns, [key]: !isExpanded });
        setSelectedConn(conn);

        if (!isExpanded && conn.ConnId) {
            setLoadingTables(true);
            request.Get(API_BASE + `superadmin/connections/${conn.ConnId}/tables`, {}, r => {
                if (r.success) setConnTables(r.data || []);
                setLoadingTables(false);
            });
        }
    };

    const handleSelectQuery = (qId) => {
        setLoadingResults(true);
        setEditorMode('saved');
        setPreviewTable(null);
        request.Get(API_BASE + `superadmin/queries/${qId}`, {}, r => {
            if (r.success && r.data.length > 0) {
                const d = r.data[0];
                
                // Sync explorer connection
                const connObj = allConns.find(c => c.ConnName.toLowerCase() === d.AutQCon?.toLowerCase());
                if (connObj) setSelectedConn(connObj);

                const formatParams = (p) => {
                    if (!p) return '';
                    if (typeof p === 'object') return JSON.stringify(p, null, 2);
                    return p;
                };

                setSelectedQuery({
                    id: d.AutQId, title: d.AutQTitle, type: d.AutQTip, con: d.AutQCon,
                    comp: d.AutQComp || '', params: formatParams(d.AutQParams),
                    fields: d.AutQIFields || '*', tables: d.AutQITables || '',
                    where: d.AutQIWhere || '', orderby: d.AutQIOrderBy || '',
                    groupby: d.AutQIGroupBy || '', limit: d.AutQILimit || '100',
                    free: d.AutQIFreeTipyng || ''
                });
                // Auto-test on select
                request.Post(API_BASE + 'superadmin/queries/test', d, res => {
                    if (res.success) setResults(res.data);
                    setLoadingResults(false);
                });
            } else {
                setLoadingResults(false);
            }
        });
    };

    const handlePreviewTable = (tableName) => {
        setPreviewTable(tableName);
        setLoadingResults(true);
        setResults(null);
        request.Post(API_BASE + 'superadmin/queries/test', {
            con: selectedConn.ConnName, type: 'NORM', fields: '*', tables: tableName, limit: '100'
        }, r => {
            if (r.success) setResults(r.data || []);
            else Notify({ type: 'fail', text: r.msg });
            setLoadingResults(false);
        });
    };

    const runExecution = () => {
        setLoadingResults(true);
        setResults(null);
        const data = editorMode === 'saved' 
            ? { ...selectedQuery, con: selectedConn.ConnName } 
            : { con: selectedConn.ConnName, type: 'DROP', free: playgroundSQL };

        request.Post(API_BASE + 'superadmin/queries/test', data, r => {
            if (r.success) setResults(r.data || []);
            else Notify({ type: 'fail', text: r.msg });
            setLoadingResults(false);
        });
    };

    const handleSave = () => {
        if (!selectedQuery?.title) return Notify({ type: 'fail', text: 'Título requerido' });
        
        // Force current connection from explorer
        const queryToSave = { ...selectedQuery, con: selectedConn.ConnName };
        
        const method = selectedQuery.id ? 'Put' : 'Post';
        const url = selectedQuery.id ? `superadmin/queries/${selectedQuery.id}` : 'superadmin/queries';
        
        request[method](API_BASE + url, queryToSave, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Consulta guardada correctamente' });
                fetchQueries();
                // Update local state to reflect saved connection
                setSelectedQuery(queryToSave);
            } else {
                Notify({ type: 'fail', text: 'Error al guardar consulta' });
            }
        });
    };

    const handleDelete = (id) => {
        if (!window.confirm('¿Eliminar esta consulta permanentemente?')) return;
        request.Delete(API_BASE + `superadmin/queries/${id}`, {}, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Consulta eliminada' });
                fetchQueries();
                setSelectedQuery(null);
            }
        });
    };

    const allConns = [
        //{ ConnName: 'locU', isLocal: true },
        //{ ConnName: 'conU', isLocal: true },
        ...connections
    ];

    return (
        <div className="qb-master-container animate-in">
            {/* 1. TOP BAR */}
            <div className="qb-top-bar">
                <div className="d-flex gap-3">
                    <button className={`qb-tab ${activeMainTab === 'explorer' ? 'active' : ''}`} onClick={() => setActiveMainTab('explorer')}>
                        <FontAwesomeIcon icon={faColumns} className="me-2" /> IDE: Query & Explorer
                    </button>
                    <button className={`qb-tab ${activeMainTab === 'connections' ? 'active' : ''}`} onClick={() => setActiveMainTab('connections')}>
                        <FontAwesomeIcon icon={faNetworkWired} className="me-2" /> Conexiones Externas
                    </button>
                </div>
                <div className="qb-premium-badge">
                    <FontAwesomeIcon icon={faMagic} className="me-2" /> OctoCore Engine v2.1
                </div>
            </div>

            <div className="qb-main-layout">
                <AnimatePresence mode="wait">
                    {activeMainTab === 'connections' ? (
                        <motion.div key="conn" className="w-100 p-4 overflow-auto"><ConnectionManager /></motion.div>
                    ) : (
                        <motion.div key="ide" className="qb-ide-grid">

                            {/* ─── COL 1: EXPLORER ─── */}
                            <div className="qb-col qb-col-explorer">
                                <div className="p-3 border-bottom border-secondary border-opacity-10">
                                    <input
                                        type="text" className="sa-input sa-input-sm qb-glass" placeholder="Filtrar..."
                                        value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                    />
                                </div>
                                <div className="qb-explorer-scroll custom-scrollbar">
                                    {allConns.filter(c => c.ConnName.toLowerCase().includes(searchTerm.toLowerCase())).map(conn => {
                                        const key = conn.ConnId || conn.ConnName;
                                        const isExpanded = expandedConns[key];
                                        const connQueries = queries.filter(q => q.AutQCon?.toLowerCase() === conn.ConnName.toLowerCase());
                                        return (
                                            <div key={key} className="mb-1">
                                                <div className={`qb-item conn ${selectedConn?.ConnName === conn.ConnName ? 'active' : ''}`} onClick={() => toggleConn(conn)}>
                                                    <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="me-2" />
                                                    <FontAwesomeIcon icon={faDatabase} className="me-2 text-primary" />
                                                    <span className="text-truncate">{conn.ConnName}</span>
                                                </div>
                                                {isExpanded && (
                                                    <div className="ms-3 ps-2 border-start border-secondary border-opacity-10 mt-1">
                                                        <div className="qb-sub-header">Consultas <button className="ms-auto btn-plus" onClick={() => { setSelectedQuery(emptyQuery(conn.ConnName)); setEditorMode('saved'); setPreviewTable(null); }}><FontAwesomeIcon icon={faPlus} /></button></div>
                                                        {connQueries.map(q => (
                                                            <div key={q.AutQId} className={`qb-item query ${selectedQuery?.id === q.AutQId ? 'active' : ''}`} onClick={() => handleSelectQuery(q.AutQId)}>
                                                                <FontAwesomeIcon icon={faFileCode} className="me-2 text-purple" />
                                                                <span className="text-truncate">{q.AutQTitle}</span>
                                                                <button className="ms-auto qb-del-btn" onClick={(e) => { e.stopPropagation(); handleDelete(q.AutQId); }}><FontAwesomeIcon icon={faTrash} /></button>
                                                            </div>
                                                        ))}
                                                        {!conn.isLocal && (
                                                            <>
                                                                <div className="qb-sub-header mt-2">Tablas</div>
                                                                {loadingTables && selectedConn?.ConnId === conn.ConnId ? <Spinner size="xs" animation="border" className="ms-3" /> :
                                                                    connTables.map(t => <div key={t} className={`qb-item table ${previewTable === t ? 'active' : ''}`} onClick={() => handlePreviewTable(t)}><FontAwesomeIcon icon={faTable} className="me-2 text-success" /> {t}</div>)
                                                                }
                                                            </>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ─── COL 2: EDITOR ─── */}
                            <div className="qb-col qb-col-editor  bg-opacity-10">
                                <div className="p-3 border-bottom border-secondary border-opacity-10 d-flex gap-2">
                                    <button className={`qb-mode-btn ${editorMode === 'saved' ? 'active' : ''}`} onClick={() => setEditorMode('saved')}>Editor AUTQ</button>
                                    <button className={`qb-mode-btn ${editorMode === 'playground' ? 'active' : ''}`} onClick={() => setEditorMode('playground')}>Playground</button>
                                    <button className="sa-btn sa-btn-primary btn-sm ms-auto" onClick={runExecution} disabled={loadingResults}><FontAwesomeIcon icon={loadingResults ? faSpinner : faPlay} spin={loadingResults} className="me-1" /> Ejecutar</button>
                                </div>

                                <div className="p-4 flex-grow-1 overflow-auto custom-scrollbar">
                                    <AnimatePresence mode="wait">
                                        {editorMode === 'saved' ? (
                                            selectedQuery ? (
                                                <motion.div key="saved" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="d-flex flex-column gap-3">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <div className="d-flex align-items-center gap-3">
                                                            <h6 className="m-0 fw-bold text-primary"><FontAwesomeIcon icon={faCube} className="me-2" /> Constructor</h6>
                                                            {selectedQuery.id && (
                                                                <div className="sa-badge-id">
                                                                    <span className="opacity-50 me-1">AUTQ_ID:</span>
                                                                    <span className="fw-bold">{selectedQuery.id}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <button className="sa-btn sa-btn-primary btn-sm" onClick={handleSave}><FontAwesomeIcon icon={faSave} className="me-2" /> Guardar Cambios</button>
                                                    </div>

                                                    <Row className="g-2">
                                                        <Col md={5}><label className="sa-label">AutQ Title</label><input type="text" className="sa-input" placeholder="Nombre descriptivo" value={selectedQuery.title} onChange={e => setSelectedQuery({ ...selectedQuery, title: e.target.value })} /></Col>
                                                        <Col md={4}><label className="sa-label">Component Path (/Component)</label><input type="text" className="sa-input" placeholder="/Sistema/Turnero/..." value={selectedQuery.comp} onChange={e => setSelectedQuery({ ...selectedQuery, comp: e.target.value })} /></Col>
                                                        <Col md={3}><label className="sa-label">Tipo</label><select className="sa-input" value={selectedQuery.type} onChange={e => setSelectedQuery({ ...selectedQuery, type: e.target.value })}><option value="NORM">NORM</option><option value="DROP">DROP</option><option value="CONCAT">CONCAT</option></select></Col>
                                                    </Row>

                                                    <Row className="g-2">
                                                        <Col md={12}>
                                                            <label className="sa-label">JS Parameters (AutQParams)</label>
                                                            <textarea className="sa-input font-monospace" rows={2} placeholder="[ { 'key': 'value' } ] o código JS..." value={selectedQuery.params} onChange={e => setSelectedQuery({ ...selectedQuery, params: e.target.value })} />
                                                        </Col>
                                                    </Row>

                                                    <div className="sa-panel p-3 qb-glass">
                                                        {selectedQuery.type === 'NORM' ? (
                                                            <Row className="g-3">
                                                                <Col md={12}><label className="sa-label small text-primary">SELECT (Campos)</label><textarea className="sa-input font-monospace" rows={2} value={selectedQuery.fields} onChange={e => setSelectedQuery({ ...selectedQuery, fields: e.target.value })} /></Col>
                                                                <Col md={12}><label className="sa-label small text-primary">FROM (Tablas)</label><input type="text" className="sa-input font-monospace" value={selectedQuery.tables} onChange={e => setSelectedQuery({ ...selectedQuery, tables: e.target.value })} /></Col>
                                                                <Col md={12}><label className="sa-label small text-primary">WHERE (Filtros)</label><textarea className="sa-input font-monospace" rows={2} value={selectedQuery.where} onChange={e => setSelectedQuery({ ...selectedQuery, where: e.target.value })} /></Col>
                                                                <Col md={4}><label className="sa-label small">GROUP BY</label><input type="text" className="sa-input font-monospace" value={selectedQuery.groupby} onChange={e => setSelectedQuery({ ...selectedQuery, groupby: e.target.value })} /></Col>
                                                                <Col md={4}><label className="sa-label small">ORDER BY</label><input type="text" className="sa-input font-monospace" value={selectedQuery.orderby} onChange={e => setSelectedQuery({ ...selectedQuery, orderby: e.target.value })} /></Col>
                                                                <Col md={4}><label className="sa-label small">LIMIT</label><input type="text" className="sa-input font-monospace" value={selectedQuery.limit} onChange={e => setSelectedQuery({ ...selectedQuery, limit: e.target.value })} /></Col>
                                                            </Row>
                                                        ) : <textarea className="qb-code-area" style={{ height: 350 }} value={selectedQuery.free} onChange={e => setSelectedQuery({ ...selectedQuery, free: e.target.value })} />}
                                                    </div>
                                                </motion.div>
                                            ) : <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted opacity-40"><FontAwesomeIcon icon={faDatabase} size="3x" className="mb-3" /><p>Selecciona una consulta del explorador</p></div>
                                        ) : (
                                            <motion.div key="play" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-100 d-flex flex-column">
                                                <h6 className="mb-3 fw-bold text-purple"><FontAwesomeIcon icon={faTerminal} className="me-2" /> Consola Playground ({selectedConn?.ConnName})</h6>
                                                <textarea className="qb-code-area flex-grow-1" value={playgroundSQL} onChange={e => setPlaygroundSQL(e.target.value)} />
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>

                            {/* ─── COL 3: RESULTS ─── */}
                            <div className="qb-col qb-col-results">
                                <div className="p-3 border-bottom border-secondary border-opacity-10 d-flex justify-content-between align-items-center  bg-opacity-20">
                                    <span className="small fw-bold text-muted">RESULTADOS {previewTable && `[${previewTable}]`}</span>
                                    {results && <button className="btn btn-link text-success btn-sm p-0" ><FontAwesomeIcon icon={faFileExcel} className="me-1" /> Exportar CSV</button>}
                                </div>
                                <div className="flex-grow-1 overflow-auto custom-scrollbar">
                                    {loadingResults ? <div className="h-100 d-flex align-items-center justify-content-center"><Spinner animation="border" variant="primary" /></div> :
                                        results ? (
                                            <Table responsive hover className="sa-table m-0 qb-grid">
                                                <thead className="sticky-top "><tr>{Object.keys(results[0] || {}).map(k => <th key={k}>{k}</th>)}</tr></thead>
                                                <tbody>{results.map((row, i) => <tr key={i}>{Object.values(row).map((v, j) => <td key={j}>{v === null ? '-' : String(v)}</td>)}</tr>)}</tbody>
                                            </Table>
                                        ) : <div className="h-100 d-flex align-items-center justify-content-center text-muted opacity-10"><FontAwesomeIcon icon={faHistory} size="4x" /></div>
                                    }
                                </div>
                            </div>

                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            <style>{`
                .qb-master-container { height: calc(100vh - 80px); display: flex; flex-direction: column; background: var(--bg-main); }
                .qb-top-bar { display: flex; align-items: center; justify-content: space-between; padding: 0.8rem 2rem; border-bottom: 1px solid var(--card-border); background: var(--card-bg); }
                .qb-tab { background: none; border: none; color: var(--text-muted); font-weight: 700; padding: 10px 15px; border-radius: 8px; transition: all 0.2s; font-size: 0.85rem; }
                .qb-tab.active { background: rgba(26, 115, 232, 0.1); color: var(--primary); }
                .qb-premium-badge { background: rgba(26, 115, 232, 0.05); color: var(--primary); font-size: 0.65rem; font-weight: 800; padding: 4px 15px; border-radius: 20px; border: 1px solid rgba(26, 115, 232, 0.1); }

                .qb-main-layout { flex-grow: 1; overflow: hidden; display: flex; }
                .qb-ide-grid { display: grid; grid-template-columns: 260px 1fr 1fr; width: 100%; height: 100%; gap: 1px; background: var(--border-color); }
                
                .qb-col { display: flex; flex-direction: column; overflow: hidden; background: #ffffff; }
                .qb-explorer-scroll { flex-grow: 1; overflow-y: auto; padding: 10px; }
                
                .sa-badge-id { background: rgba(0, 97, 255, 0.08); color: var(--primary); padding: 4px 12px; border-radius: 6px; font-size: 0.7rem; border: 1px solid rgba(0, 97, 255, 0.15); font-family: 'JetBrains Mono', monospace; }

                .qb-item { display: flex; align-items: center; padding: 8px 12px; border-radius: 8px; cursor: pointer; font-size: 0.78rem; color: var(--text-muted); transition: all 0.15s; margin-bottom: 2px; border: 1px solid transparent; }
                .qb-item:hover { background: var(--); color: var(--text-main); border-color: var(--card-border); }
                .qb-item.active { background: rgba(26, 115, 232, 0.1); color: var(--primary); font-weight: 700; border: 1px solid rgba(26, 115, 232, 0.2); }
                .qb-del-btn { opacity: 0; background: none; border: none; color: #ef4444; font-size: 0.7rem; padding: 2px 5px; }
                .qb-item:hover .qb-del-btn { opacity: 1; }
                
                .qb-sub-header { padding: 8px 12px; font-size: 0.6rem; font-weight: 800; color: var(--text-dim); display: flex; align-items: center; text-transform: uppercase; letter-spacing: 0.5px; opacity: 0.8; }
                .btn-plus { background: var(--primary); border: none; color: white; width: 16px; height: 16px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.55rem; }

                .qb-mode-btn { background: none; border: none; padding: 5px 12px; color: var(--text-muted); font-size: 0.75rem; font-weight: 700; border-radius: 6px; }
                .qb-mode-btn.active { background: #f1f5f9; color: var(--text-main); }

                .qb-code-area { background: #ffffff !important; color: var(--primary) !important; border: 1px solid var(--card-border) !important; border-radius: 12px; padding: 15px; font-family: 'JetBrains Mono', monospace; font-size: 0.82rem; line-height: 1.6; width: 100%; resize: none; }
                .qb-glass { background: #ffffff !important; border: 1px solid var(--card-border) !important; border-radius: 12px; }

                .qb-grid { font-size: 0.75rem; white-space: nowrap; border-collapse: collapse; }
                .qb-grid thead th { background: #f8fafc !important; color: var(--text-muted); padding: 12px; border-bottom: 2px solid var(--border-color) !important; text-transform: uppercase; border-right: 1px solid var(--border-color); }
                .qb-grid td { padding: 10px 12px; border-bottom: 1px solid var(--border-color); border-right: 1px solid var(--border-color); max-width: 220px; overflow: hidden; text-overflow: ellipsis; color: var(--text-main); }
            `}</style>
        </div>
    );
};

const emptyQuery = (conn) => ({ title: '', type: 'NORM', con: conn, comp: '', params: '', fields: '*', tables: '', where: '', orderby: '', groupby: '', limit: '100', free: '' });

export default QueryBuilderPanel;
